"use client";

import React from 'react';
import { motion } from "framer-motion";

export interface ProductHeroBlockProps {
    title?: string;
    description?: string;
    image?: string;
    metadata?: { label: string; value: string }[];
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

const LaptopMockup = ({ imageSrc }: { imageSrc: string }) => (
    <motion.div 
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-[700px] mx-auto relative group perspective-[1200px]"
    >
        {/* Screen Top Half */}
        <div className="relative rounded-t-[24px] bg-black p-[8px] pb-0 border-t border-x border-zinc-800 shadow-2xl z-10 origin-bottom transition-transform duration-700 ease-out group-hover:rotate-x-[2deg]">
            {/* Screen Inner Bezel */}
            <div className="w-full bg-zinc-900 rounded-t-[16px] overflow-hidden relative aspect-[16/10]">
                {/* Camera Hole */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-black rounded-full z-20 border border-zinc-800" />
                
                {/* Screenshot */}
                {imageSrc && (
                    <img 
                        src={imageSrc} 
                        alt="Desktop Mockup" 
                        className="w-full h-full object-cover object-top"
                    />
                )}
                
                {/* Glare */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
            </div>
            {/* Bottom Bezel (Chin) */}
            <div className="h-7 w-full bg-black rounded-b-[8px] relative flex items-center justify-center border-b border-zinc-900">
                <span className="text-[6px] text-zinc-600 font-bold tracking-widest font-sans">KITEBE</span>
            </div>
        </div>

        {/* Base / Keyboard Deck */}
        <div className="relative h-[12px] w-[105%] -ml-[2.5%] bg-gradient-to-b from-[#e5e5ea] to-[#b0b0b5] rounded-b-[16px] shadow-2xl z-0 flex justify-center border-t border-white/20">
            {/* Thumb Cutout */}
            <div className="w-24 h-1.5 bg-[#a0a0a5] rounded-b-md" />
        </div>
        
        {/* Shadow / Glow */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[80%] h-12 bg-black/60 blur-2xl z-[-1]" />
    </motion.div>
);

export default function ProductHeroBlock({
    title = "CREATING A DIGITAL GATEWAY\nFOR JEDDAH’S PREMIER\nBOUTIQUE HOTEL & SUITES",
    description = "Crown Town Hotel embodies modern grandeur in the Al Hamra district of Jeddah. We crafted a premium digital gateway that replaces clinical booking forms with atmospheric storytelling, matching the physical luxury of their boutique rooms and wellness spa.",
    image = "/images/crowntown_desktop_full.png",
    metadata = [
        { label: "CLIENT / TYPE", value: "Crown Town Hotel / Luxury Hospitality" },
        { label: "SERVICES / AREA", value: "Digital Strategy, UI/UX Design, Development" },
        { label: "YEAR / DURATION", value: "2026 / 8 Weeks" },
        { label: "CLIENT / LOCATION", value: "Crown Town, Jeddah" }
    ],
    style = {}
}: ProductHeroBlockProps) {
    const bg = style.backgroundColor || "#09090A";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    return (
        <section 
            className="relative px-6 md:px-12 lg:px-24 xl:px-32 border-b border-zinc-900/60 bg-[radial-gradient(var(--bg-radial-dot)_1px,transparent_1px)] [background-size:32px_32px] overflow-hidden"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "9rem",
                paddingBottom: style.paddingBottom || "5rem",
                // Create a CSS variable dynamically for standard colors
                "--bg-radial-dot": `${accent}08`
            } as any}
        >
            {/* Background Ambient Glows */}
            <div 
                className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[140px] pointer-events-none"
                style={{ backgroundColor: `${accent}08` }}
            />
            <div 
                className="absolute top-1/2 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none"
                style={{ backgroundColor: `${accent}05` }}
            />

            <div className="container mx-auto z-10 w-full">
                {/* Top Case Study Metadata Grid */}
                {metadata && metadata.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-16 border-b border-zinc-900/80 text-xs font-mono text-zinc-500">
                        {metadata.map((item, idx) => (
                            <div key={idx} className="flex flex-col gap-1.5">
                                <span className="uppercase tracking-wider font-semibold" style={{ color: accent }}>
                                    {item.label}
                                </span>
                                <span className="text-zinc-300">{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Main Content & Laptop */}
                <div className="pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    {/* Left: Text */}
                    <div className="lg:col-span-6 space-y-8">
                        <h1 
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-extralight tracking-wide text-white leading-[1.1] font-cormorant whitespace-pre-line"
                        >
                            {/* Check if italic/custom formatting is needed, or just split title */}
                            {title.includes('\n') ? (
                                title.split('\n').map((line, index, arr) => {
                                    if (index === arr.length - 1) {
                                        return (
                                            <span key={index} className="font-light italic" style={{ color: accent }}>
                                                {line}
                                            </span>
                                        );
                                    }
                                    return (
                                        <React.Fragment key={index}>
                                            {line} <br />
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                title
                            )}
                        </h1>

                        <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl font-light pt-4">
                            {description}
                        </p>
                    </div>
                    
                    {/* Right: Laptop Mockup */}
                    {image && (
                        <div className="lg:col-span-6 relative hidden lg:block">
                            <LaptopMockup imageSrc={image} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
